/*
 * KV cart drawer (snippets/kv-cart-drawer.liquid). Vanilla JS, no dependencies (Swiper is optional, for the empty-cart slider).
 *
 * Public API (window.KVCart):
 *   KVCart.add(items, { open = true })   items: {id, quantity, properties} | [items] | FormData. POST /cart/add.js, re-renders, opens.
 *                                        Resolves { items, cart }; rejects with { status, message, description }.
 *   KVCart.change(lineKey, quantity)     POST /cart/change.js (quantity 0 removes the line). Resolves the cart.
 *   KVCart.refresh()                     Re-fetches /cart.js (+ the drawer markup) and re-renders. Resolves the cart.
 *   KVCart.open() / KVCart.close() / KVCart.toggle() / KVCart.isOpen()
 *   KVCart.getCart()                     Last known cart JSON (or null).
 *   KVCart.formatMoney(cents)            Formats with the shop money format.
 *
 * Rendering: the drawer markup (line items, discounts, totals, empty state) is rendered by Liquid. Updates use the Section
 * Rendering API for the section that contains the drawer (bundled in add.js / change.js responses through the `sections`
 * parameter, or GET ?sections=<id> for refresh). If the drawer is not inside a section, a client-side copy of the line
 * template renders /cart.js data instead.
 *
 * Integration:
 *   - Click on any [data-kv-cart-open] (or legacy .js-drawer-open-cart) opens the drawer (default prevented).
 *   - Every [data-kv-cart-count] gets the item count as text plus a data-count attribute (style [data-count="0"] to hide).
 *   - document event "kv:cart:add" (detail: { items, open }) adds items.
 *   - Impact product forms (<form is="product-form">): "variant:add" opens the drawer with the bundled markup;
 *     other forms posting to /cart/add are submitted with AJAX (opt out with data-kv-cart-native).
 *   - "cart:refresh" / "cart:build" / "cart:change" (from other scripts) re-render; "cart:open" / "ajaxProduct:added" open.
 *   - Emits "kv:cart:updated" (detail: { cart }), "kv:cart:open", "kv:cart:close" on document, and Impact's
 *     "cart:change" (detail: { baseEvent: 'kv-cart', cart }) on <html> so native cart counters stay in sync.
 */
(function () {
  'use strict';
  if (window.KVCart && window.KVCart.__kv) return;

  var OPENERS = '[data-kv-cart-open], .js-drawer-open-cart';
  var state = { cart: null, queue: Promise.resolve(), opener: null, timer: null, swiper: null };

  function drawer() { return document.getElementById('CartDrawer'); }
  function html() { return document.documentElement; }
  function rootUrl() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || (drawer() && drawer().getAttribute('data-root-url')) || '/';
    return String(r).replace(/\/?$/, '/');
  }
  function url(path) { return rootUrl() + String(path).replace(/^\//, ''); }
  function sectionId() {
    var d = drawer();
    if (!d) return null;
    if (d.getAttribute('data-section-id')) return d.getAttribute('data-section-id');
    var s = d.closest('[id^="shopify-section-"]');
    return s ? s.id.replace(/^shopify-section-/, '') : null;
  }
  function emit(name, detail, target) {
    (target || document).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  /* ---------------- money ---------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || (drawer() && drawer().getAttribute('data-money-format')) || window.KV_MONEY_FORMAT || '${{amount}}';
    var n = Number(cents) || 0;
    function fmt(num, decimals, thousands, dec) {
      var fixed = (num / 100).toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? parts[0] + dec + parts[1] : parts[0];
    }
    var m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = fmt(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(n, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(n, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(n, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(n, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(n, 2, ' ', '.'); break;
      default: value = fmt(n, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ---------------- requests (serialised so line keys never go stale) ---------------- */
  function enqueue(task) {
    var run = state.queue.then(task, task);
    state.queue = run.catch(function () {});
    return run;
  }
  function json(res) {
    return res.text().then(function (text) {
      var body = null;
      try { body = text ? JSON.parse(text) : null; } catch (e) { body = { message: text }; }
      if (!res.ok) {
        var err = body || {};
        err.status = err.status || res.status;
        throw err;
      }
      return body;
    });
  }
  function getCartJson() {
    return fetch(url('cart.js') + '?t=' + Date.now(), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(json);
  }
  function getSections() {
    var sid = sectionId();
    if (!sid) return Promise.resolve(null);
    var sep = location.search ? '&' : '?';
    return fetch(location.pathname + location.search + sep + 'sections=' + encodeURIComponent(sid), { credentials: 'same-origin' })
      .then(json)
      .catch(function () { return null; });
  }
  function postJson(path, payload) {
    return fetch(url(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    }).then(json);
  }
  function withSections(payload) {
    var sid = sectionId();
    if (sid) {
      payload.sections = sid;
      payload.sections_url = location.pathname;
    }
    return payload;
  }

  /* ---------------- rendering ---------------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function sized(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  var MINUS = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 20 20"><path fill="#444" d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>';
  var PLUS = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 20 20"><path fill="#444" d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>';
  var REMOVE = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 8H3" stroke="#534F4F" stroke-width="0.666667" stroke-linecap="round"/></svg>';

  /* Client-side copy of snippets/kv-cart-items.liquid (used when no section id is available). */
  function lineHtml(item) {
    var unitList = Math.max(Number(item.original_price) || 0, Number(item.compare_at_price) || 0);
    var lineList = unitList * item.quantity;
    var image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
    var variants = '';
    if (!item.product_has_only_default_variant && item.options_with_values) {
      variants = '<div class="cart__item--variants">' + item.options_with_values.map(function (o) {
        return '<div><span>' + esc(o.name) + ':</span> ' + esc(String(o.value).split(' - US')[0].split(' - UK')[0]) + '</div>';
      }).join('') + '</div>';
    }
    var props = '';
    Object.keys(item.properties || {}).forEach(function (k) {
      var v = item.properties[k];
      if (v && k.charAt(0) !== '_') props += '<div><span>' + esc(k) + ':</span> ' + esc(v) + '</div>';
    });
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      props = '<div>' + esc(item.selling_plan_allocation.selling_plan.name) + '</div>' + props;
    }
    var price = lineList > item.final_line_price
      ? '<span class="visually-hidden">Regular price</span><small class="cart__price cart__price--strikethrough cart__price--compare">' + formatMoney(lineList) + '</small>' +
        '<span class="visually-hidden">Sale price</span><span class="cart__price cart__price--sale">' + formatMoney(item.final_line_price) + '</span>'
      : '<span class="cart__price">' + formatMoney(item.final_line_price) + '</span>';
    var discounts = (item.line_level_discount_allocations || []).map(function (a) {
      return '<small class="cart__discount">' + esc(a.discount_application && a.discount_application.title) + ' (-' + formatMoney(a.amount) + ')</small>';
    }).join('');
    var key = esc(item.key);
    return '<div class="cart__item" data-key="' + key + '"><div class="cart__image"><a href="' + esc(item.url) + '" style="height: 0; padding-bottom: 100.0%;">' +
      (image
        ? '<img src="' + esc(sized(image, 360)) + '" srcset="' + esc(sized(image, 180)) + ' 180w, ' + esc(sized(image, 360)) + ' 360w, ' + esc(sized(image, 540)) + ' 540w" sizes="80px" loading="lazy" alt="' + esc(item.product_title) + '">'
        : '<div class="kv-cart-placeholder"></div>') +
      '</a></div><div class="cart__item-details"><div class="cart__item-details__top" style="display:none;"><button type="button" class="cart__remove-icon" data-cart-remove data-line-key="' + key + '" aria-label="Remove">' + REMOVE + '</button></div>' +
      '<div class="cart__item-title"><a href="' + esc(item.url) + '" class="cart__item-name">' + esc(item.product_title) + '</a>' + variants + '<div class="cart__item--variants">' + props + '</div></div>' +
      '<div class="cart__item-sub"><div><div class="js-qty__wrapper"><label for="cart_updates_' + key + '" class="hidden-label">Quantity</label>' +
      '<input type="text" id="cart_updates_' + key + '" name="updates[]" class="js-qty__num" value="' + item.quantity + '" min="0" pattern="[0-9]*" data-id="' + key + '">' +
      '<button type="button" class="js-qty__adjust js-qty__adjust--minus" aria-label="Reduce item quantity by one">' + MINUS + '<span class="icon__fallback-text" aria-hidden="true">&minus;</span></button>' +
      '<button type="button" class="js-qty__adjust js-qty__adjust--plus" aria-label="Increase item quantity by one">' + PLUS + '<span class="icon__fallback-text" aria-hidden="true">+</span></button>' +
      '</div></div><div class="cart__item-price-col text-right"><div class="cart__item-price-row">' + price + '</div>' + discounts + '</div></div></div></div>';
  }

  function renderFromCart(cart) {
    var d = drawer();
    if (!d) return;
    var products = d.querySelector('[data-products]');
    if (products) {
      var compare = 0;
      (cart.items || []).forEach(function (i) {
        compare += Math.max(Number(i.original_price) || 0, Number(i.compare_at_price) || 0) * i.quantity;
      });
      products.innerHTML = '<div class="cart__items" data-count="' + cart.item_count + '" data-cart-subtotal="' + cart.total_price + '" data-cart-compare-subtotal="' + compare + '">' +
        (cart.items || []).map(lineHtml).join('') + '</div>';
    }
    var discounts = d.querySelector('[data-discounts]');
    if (discounts) {
      var apps = cart.cart_level_discount_applications || [];
      discounts.innerHTML = apps.length
        ? '<div class="cart__discounts cart__item-sub cart__item-row"><div>' + esc(discounts.getAttribute('data-label') || 'Discounts') + '</div><div class="text-right">' +
          apps.map(function (a) { return '<div class="cart__discount">' + esc(a.title) + ' (-' + formatMoney(a.total_allocated_amount) + ')</div>'; }).join('') + '</div></div>'
        : '';
    }
    syncTotals(cart);
    d.classList.toggle('is-empty', !cart.item_count);
  }

  function syncTotals(cart) {
    var d = drawer();
    if (!d || !cart) return;
    var compareEl = d.querySelector('[data-kv-drawer-subtotal-compare]');
    var subtotalEl = d.querySelector('[data-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (compareEl) {
      var show = Number(cart.original_total_price) > Number(cart.total_price);
      compareEl.textContent = show ? formatMoney(cart.original_total_price) : '';
      compareEl.classList.toggle('hide', !show);
      compareEl.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
  }

  function applySectionHtml(markup) {
    var d = drawer();
    if (!d || !markup) return false;
    var doc = new DOMParser().parseFromString(markup, 'text/html');
    var next = doc.getElementById('CartDrawer');
    if (!next) return false;
    ['[data-products]', '[data-discounts]', '.kv-cart-drawer-subtotal__values'].forEach(function (sel) {
      var a = d.querySelector(sel), b = next.querySelector(sel);
      if (a && b) a.innerHTML = b.innerHTML;
    });
    d.classList.toggle('is-empty', next.classList.contains('is-empty'));
    return true;
  }

  function setCount(n) {
    n = parseInt(n, 10);
    if (isNaN(n)) return;
    document.querySelectorAll('[data-kv-cart-count]').forEach(function (el) {
      el.textContent = String(n);
      el.setAttribute('data-count', String(n));
    });
  }

  function update(cart, sections, opts) {
    if (!cart) return cart;
    state.cart = cart;
    var sid = sectionId();
    var markup = sections && sid ? sections[sid] : null;
    if (!applySectionHtml(markup)) renderFromCart(cart);
    else syncTotals(cart);
    setCount(cart.item_count);
    upsellSync(cart);
    if (drawer() && drawer().classList.contains('is-empty') && isOpen()) mountEmptySlider();
    emit('kv:cart:updated', { cart: cart });
    if (!(opts && opts.silent)) emit('cart:change', { baseEvent: 'kv-cart', cart: cart, kv: true }, html());
    return cart;
  }

  /* ---------------- API ---------------- */
  function normaliseItems(items) {
    if (!items) return [];
    if (!Array.isArray(items)) items = [items];
    return items.map(function (i) {
      var o = { id: Number(i.id || i.variant_id), quantity: Number(i.quantity) || 1 };
      if (i.properties) o.properties = i.properties;
      if (i.selling_plan) o.selling_plan = i.selling_plan;
      return o;
    });
  }

  /* Kaching Bundles rewrites every cart/add of the product on the page into its own widget
     selection unless the URL carries kaching-cart=true (the skip flag in its CartInterceptor). The KV buy box
     already sends the exact variants per pack; Kaching still applies the quantity discount in the cart. */
  var ADD_PATH = 'cart/add.js?kaching-cart=true';

  function add(items, opts) {
    opts = opts || {};
    return enqueue(function () {
      var req;
      if (items instanceof FormData) {
        var sid = sectionId();
        if (sid) { items.set('sections', sid); items.set('sections_url', location.pathname); }
        req = fetch(url(ADD_PATH), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: items }).then(json);
      } else {
        req = postJson(ADD_PATH, withSections({ items: normaliseItems(items) }));
      }
      return req.then(function (res) {
        return getCartJson().then(function (cart) {
          update(cart, res && res.sections);
          emit('kv:cart:added', { items: res && res.items ? res.items : [res], cart: cart });
          if (opts.open !== false) open();
          return { items: res && res.items ? res.items : [res], cart: cart };
        });
      });
    });
  }

  function change(key, quantity) {
    return enqueue(function () {
      return postJson('cart/change.js', withSections({ id: String(key), quantity: Math.max(0, parseInt(quantity, 10) || 0) }))
        .then(function (cart) { return update(cart, cart && cart.sections); });
    });
  }

  function refresh() {
    return enqueue(function () {
      return Promise.all([getCartJson(), getSections()]).then(function (r) { return update(r[0], r[1]); });
    });
  }

  /* ---------------- open / close ---------------- */
  function isOpen() { var d = drawer(); return !!(d && d.classList.contains('drawer--is-open')); }

  function prepareTransition(d) {
    function done(e) { if (e.target !== d) return; d.classList.remove('is-transitioning'); d.removeEventListener('transitionend', done); }
    d.addEventListener('transitionend', done);
    d.classList.add('is-transitioning');
    void d.offsetWidth;
  }

  function open(opener) {
    var d = drawer();
    if (!d || isOpen()) return;
    if (opener && opener.nodeType === 1) { state.opener = opener; opener.setAttribute('aria-expanded', 'true'); }
    prepareTransition(d);
    d.classList.add('drawer--is-open');
    d.setAttribute('aria-hidden', 'false');
    html().classList.remove('kv-cart-closing');
    html().classList.add('kv-cart-open');
    try { d.focus({ preventScroll: true }); } catch (e) {}
    if (d.classList.contains('is-empty')) mountEmptySlider();
    emit('kv:cart:open');
  }

  function close() {
    var d = drawer();
    if (!d || !isOpen()) return;
    prepareTransition(d);
    d.classList.remove('drawer--is-open');
    d.setAttribute('aria-hidden', 'true');
    html().classList.remove('kv-cart-open');
    html().classList.add('kv-cart-closing');
    setTimeout(function () { html().classList.remove('kv-cart-closing'); }, 500);
    if (state.opener) {
      state.opener.setAttribute('aria-expanded', 'false');
      try { state.opener.focus({ preventScroll: true }); } catch (e) {}
      state.opener = null;
    }
    emit('kv:cart:close');
  }

  /* ---------------- empty-cart slider ---------------- */
  function mountEmptySlider() {
    var el = drawer() && drawer().querySelector('.kv-cart-drawer-empty-swiper');
    if (!el) return;
    var go = function () {
      if (!window.Swiper) return;
      if (state.swiper && state.swiper.el === el) { state.swiper.update(); return; }
      state.swiper = new window.Swiper(el, {
        slidesPerView: 2.01,
        spaceBetween: 16,
        watchOverflow: true,
        pagination: { el: el.querySelector('.swiper-pagination'), type: 'progressbar' },
      });
    };
    if (window.Swiper) return go();
    var tries = 0;
    var t = setInterval(function () { tries++; if (window.Swiper) { clearInterval(t); go(); } else if (tries > 80) clearInterval(t); }, 50);
  }

  /* ---------------- reservation timer (counts down once from page load, like the original) ---------------- */
  function startTimer() {
    var d = drawer();
    if (!d || state.timer) return;
    var total = parseInt(d.getAttribute('data-timer-seconds'), 10);
    if (isNaN(total)) return;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var out = document.querySelectorAll('[data-kv-cart-timer]');
      out.forEach(function (o) { o.textContent = pad(Math.floor(total / 60)) + ':' + pad(total % 60); });
      if (total > 0) total--; else { clearInterval(state.timer); }
    }
    tick();
    state.timer = setInterval(tick, 1000);
  }

  /* ---------------- upsell card (port of kv-pairs-3) ---------------- */
  var ZERO_DECIMAL = { JPY: 1, KRW: 1, VND: 1, CLP: 1, HUF: 1, ISK: 1 };
  function upsellRoot() { return drawer() && drawer().querySelector('[data-kv-pairs-3]'); }
  function upsellAmount(cents, root) {
    var currency = root.getAttribute('data-currency-code') || 'USD';
    var zero = !!ZERO_DECIMAL[currency];
    var n = Number(cents) || 0;
    try {
      return new Intl.NumberFormat(root.getAttribute('data-locale') || undefined, {
        style: 'decimal', minimumFractionDigits: zero ? 0 : 2, maximumFractionDigits: zero ? 0 : 2,
      }).format(zero ? n : n / 100);
    } catch (e) {
      return zero ? String(Math.round(n)) : (n / 100).toFixed(2);
    }
  }
  function upsellSync(cart) {
    var root = upsellRoot();
    if (!root || !cart) return;
    var trigger = (root.getAttribute('data-trigger') || '').toLowerCase();
    var pid = String(root.getAttribute('data-product-id'));
    var items = cart.items || [];
    var hasTrigger = items.some(function (i) {
      return !trigger || String(i.handle || '').toLowerCase().indexOf(trigger) !== -1 || String(i.url || '').toLowerCase().indexOf(trigger) !== -1;
    });
    var hasProduct = items.some(function (i) { return String(i.product_id) === pid; });
    root.hidden = !(hasTrigger && !hasProduct);
    var banner = root.querySelector('[data-kv-pairs-3-banner]');
    if (banner) banner.hidden = !hasTrigger;
    if (root.kvSync) root.kvSync();
  }
  function upsellInit() {
    var root = upsellRoot();
    if (!root || root.getAttribute('data-kv-bound') === '1') return;
    root.setAttribute('data-kv-bound', '1');
    var variants = [];
    try { variants = JSON.parse(root.querySelector('[data-kv-pairs-3-variants]').textContent || '[]'); } catch (e) {}
    var colorIndex = root.getAttribute('data-color-index') === '' ? null : parseInt(root.getAttribute('data-color-index'), 10);
    var sizeIndex = root.getAttribute('data-size-index') === '' ? null : parseInt(root.getAttribute('data-size-index'), 10);
    if (isNaN(colorIndex)) colorIndex = null;
    if (isNaN(sizeIndex)) sizeIndex = null;
    var qty = parseInt(root.getAttribute('data-quantity'), 10) || 3;
    var pct = parseInt(root.getAttribute('data-discount-pct'), 10);
    if (isNaN(pct) || pct < 0) pct = 0;
    var saveLabel = root.getAttribute('data-save-label') || '';
    var imageEl = root.querySelector('[data-kv-pairs-3-image]');
    var mediaEl = root.querySelector('.kv-pairs-3__media');
    var priceEl = root.querySelector('[data-kv-pairs-3-price]');
    var compareEl = root.querySelector('[data-kv-pairs-3-compare]');
    var saveEl = root.querySelector('[data-kv-pairs-3-save]');
    var addBtn = root.querySelector('[data-kv-pairs-3-add]');
    var all = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    var selected = function (sel) { var b = root.querySelector(sel + '.is-selected'); return b ? b.getAttribute('data-value') : null; };
    function select(buttons, clicked) {
      buttons.forEach(function (b) { var on = b === clicked; b.classList.toggle('is-selected', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
    }
    function find(c, s) {
      return variants.find(function (v) {
        if (colorIndex != null && c && v.options[colorIndex] !== c) return false;
        if (sizeIndex != null && s && v.options[sizeIndex] !== s) return false;
        return true;
      }) || null;
    }
    function sizeAvailability(c) {
      if (sizeIndex == null) return;
      all('[data-kv-pairs-3-size]').forEach(function (b) {
        var val = b.getAttribute('data-value');
        var ok = variants.some(function (v) {
          if (colorIndex != null && c && v.options[colorIndex] !== c) return false;
          return v.options[sizeIndex] === val && v.available;
        });
        b.disabled = !ok;
        b.setAttribute('aria-disabled', ok ? 'false' : 'true');
        b.classList.toggle('is-oos', !ok);
      });
    }
    function setImage(src) {
      if (!src) return;
      if (!imageEl && mediaEl) {
        mediaEl.innerHTML = '';
        imageEl = document.createElement('img');
        imageEl.setAttribute('data-kv-pairs-3-image', '');
        imageEl.alt = '';
        imageEl.loading = 'lazy';
        mediaEl.appendChild(imageEl);
      }
      if (imageEl) { imageEl.removeAttribute('srcset'); imageEl.src = src; }
    }
    function prices(v) {
      var list = Math.max(Number(v.price) || 0, Number(v.compare_at_price) || 0) * qty;
      var sale = pct ? Math.round((list * (100 - pct)) / 100) : list;
      if (compareEl) compareEl.textContent = upsellAmount(list, root);
      if (priceEl) priceEl.textContent = upsellAmount(sale, root);
      if (saveEl) { saveEl.textContent = saveLabel; saveEl.hidden = pct <= 0; }
    }
    function sync() {
      var c = selected('[data-kv-pairs-3-color]');
      sizeAvailability(c);
      var sb = root.querySelector('[data-kv-pairs-3-size].is-selected');
      if (sb && sb.disabled) {
        var first = root.querySelector('[data-kv-pairs-3-size]:not([disabled])');
        if (first) select(all('[data-kv-pairs-3-size]'), first);
      }
      var s = selected('[data-kv-pairs-3-size]');
      var v = colorIndex != null && !c ? null : find(c, s);
      if (!v) { if (addBtn) { addBtn.disabled = true; addBtn.removeAttribute('data-variant-id'); } return; }
      prices(v);
      var img = v.featured_image;
      if (!img && colorIndex != null && c) {
        var m = variants.find(function (x) { return x.options[colorIndex] === c && x.featured_image; });
        img = m ? m.featured_image : null;
      }
      setImage(img);
      if (addBtn) { addBtn.setAttribute('data-variant-id', String(v.id)); addBtn.disabled = !v.available; }
    }
    root.addEventListener('click', function (e) {
      var c = e.target.closest('[data-kv-pairs-3-color]');
      if (c && !c.disabled) { select(all('[data-kv-pairs-3-color]'), c); sync(); return; }
      var s = e.target.closest('[data-kv-pairs-3-size]');
      if (s && !s.disabled) { select(all('[data-kv-pairs-3-size]'), s); sync(); return; }
      var btn = e.target.closest('[data-kv-pairs-3-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var vid = btn.getAttribute('data-variant-id');
      if (!vid) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = '…';
      add([{ id: Number(vid), quantity: qty }])
        .catch(function (err) { alert((err && (err.description || err.message)) || root.getAttribute('data-error') || 'Error'); })
        .then(function () { btn.disabled = false; btn.textContent = label; });
    });
    root.kvSync = sync;
    sync();
  }

  /* ---------------- event wiring ---------------- */
  function onClick(e) {
    var d = drawer();
    var opener = e.target.closest && e.target.closest(OPENERS);
    if (opener && !(d && d.contains(opener))) {
      e.preventDefault();
      isOpen() ? close() : open(opener);
      return;
    }
    if (!d) return;
    if (e.target.closest('[data-kv-cart-close]') && d.contains(e.target)) { e.preventDefault(); close(); return; }
    if (isOpen() && !d.contains(e.target) && document.contains(e.target)) {
      // outside click (overlay or page) closes, like Impulse
      close();
      return;
    }
    if (!d.contains(e.target)) return;

    var qtyBtn = e.target.closest('.js-qty__adjust--plus, .js-qty__adjust--minus');
    if (qtyBtn) {
      e.preventDefault();
      var wrapper = qtyBtn.closest('.js-qty__wrapper');
      var input = wrapper && wrapper.querySelector('.js-qty__num');
      if (!input || wrapper.classList.contains('is-loading')) return;
      var cur = parseInt(input.value, 10);
      if (isNaN(cur)) cur = 1;
      var next = Math.max(0, cur + (qtyBtn.classList.contains('js-qty__adjust--plus') ? 1 : -1));
      input.value = next;
      wrapper.classList.add('is-loading');
      change(input.getAttribute('data-id'), next)
        .catch(function (err) { console.warn('[KVCart] change failed', err); return refresh(); })
        .then(function () { if (wrapper.isConnected) wrapper.classList.remove('is-loading'); });
      return;
    }
    var rm = e.target.closest('[data-cart-remove]');
    if (rm) {
      e.preventDefault();
      if (rm.classList.contains('is-loading')) return;
      var row = rm.closest('.cart__item');
      rm.classList.add('is-loading');
      rm.disabled = true;
      if (row) row.classList.add('is-removing');
      change(rm.getAttribute('data-line-key'), 0)
        .catch(function () { return refresh(); })
        .then(function () { if (row && row.isConnected) row.classList.remove('is-removing'); });
    }
  }

  function onQtyInput(e) {
    var input = e.target;
    if (!input.classList || !input.classList.contains('js-qty__num') || !drawer() || !drawer().contains(input)) return;
    var n = parseInt(input.value, 10);
    if (isNaN(n) || n < 0) n = 0;
    var wrapper = input.closest('.js-qty__wrapper');
    if (wrapper) wrapper.classList.add('is-loading');
    change(input.getAttribute('data-id'), n).catch(function () { return refresh(); }).then(function () {
      if (wrapper && wrapper.isConnected) wrapper.classList.remove('is-loading');
    });
  }

  function onSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?(\?|$)/.test(action)) return;
    if (form.hasAttribute('is') || form.hasAttribute('data-kv-cart-native') || form.closest('[data-kv-cart-native]')) return;
    if (e.submitter && (e.submitter.name === 'checkout' || e.submitter.hasAttribute('data-kv-cart-native'))) return;
    e.preventDefault();
    var btns = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
    btns.forEach(function (b) { b.setAttribute('aria-busy', 'true'); b.disabled = true; });
    add(new FormData(form))
      .catch(function (err) {
        emit('cart:error', { error: err && (err.description || err.message) }, form);
        console.warn('[KVCart] add failed', err);
      })
      .then(function () { btns.forEach(function (b) { b.removeAttribute('aria-busy'); b.disabled = false; }); });
  }

  function bind() {
    if (html().getAttribute('data-kv-cart-bound') === '1') return;
    html().setAttribute('data-kv-cart-bound', '1');
    document.addEventListener('click', onClick);
    document.addEventListener('change', onQtyInput);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('keyup', function (e) { if ((e.key === 'Escape' || e.keyCode === 27) && isOpen()) close(); });

    document.addEventListener('kv:cart:add', function (e) {
      var d = e.detail || {};
      add(d.items, { open: d.open !== false }).catch(function (err) { console.warn('[KVCart] add failed', err); });
    });
    // Impact product forms: ask them to bundle our section, then render + open on success.
    document.addEventListener('cart:prepare-bundled-sections', function (e) {
      var sid = sectionId();
      if (sid && e.detail && Array.isArray(e.detail.sections) && e.detail.sections.indexOf(sid) === -1) e.detail.sections.push(sid);
    });
    document.addEventListener('variant:add', function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart) { update(cart, cart.sections, { silent: true }); open(); }
      else refresh().then(function () { open(); });
    });
    document.addEventListener('cart:change', function (e) {
      var d = e.detail || {};
      if (d.kv || d.baseEvent === 'variant:add') return;
      if (d.cart) setCount(d.cart.item_count);
      refresh();
    });
    ['cart:refresh', 'cart:build'].forEach(function (n) { document.addEventListener(n, function () { refresh(); }); });
    document.addEventListener('cart:open', function () { open(); });
    document.addEventListener('cart:close', function () { close(); });
    document.addEventListener('ajaxProduct:added', function () { refresh().then(function () { open(); }); });
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('#CartDrawer')) { removeMoved(); init(); }
    });
    // Theme editor: selecting the "KV Cart drawer" section (or one of its settings) opens the drawer.
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('#CartDrawer')) open();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('#CartDrawer')) close();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var d = drawer();
      if (d && e.target && e.target.id === 'shopify-section-' + d.getAttribute('data-section-id')) removeMoved();
    });
  }

  function init() {
    var d = drawer();
    if (!d) return;
    // Remember the section id. When the drawer is rendered inside another section (legacy: the header), move drawer +
    // overlay to <body> so a header with transform/backdrop-filter/z-index cannot trap the fixed layers. Its own
    // "KV Cart drawer" section (Overlay group) stays in place so the theme editor can select it.
    var sec = d.closest('[id^="shopify-section-"]');
    if (sec) d.setAttribute('data-section-id', sec.id.replace(/^shopify-section-/, ''));
    var ownSection = sec && sec.classList.contains('kv-cart-drawer-section');
    if (!ownSection && d.parentNode !== document.body) {
      var overlay = d.parentNode.querySelector('[data-kv-cart-overlay]');
      document.body.appendChild(d);
      if (overlay) document.body.appendChild(overlay);
      d.setAttribute('data-kv-moved', '1');
    }
    upsellInit();
    startTimer();
    if (state.cart) upsellSync(state.cart);
    getCartJson().then(function (cart) {
      state.cart = cart;
      setCount(cart.item_count);
      upsellSync(cart);
    }).catch(function () {});
  }

  function removeMoved() {
    document.querySelectorAll('body > #CartDrawer[data-kv-moved], body > [data-kv-cart-overlay]').forEach(function (n) { n.remove(); });
    html().classList.remove('kv-cart-open', 'kv-cart-closing');
    state.swiper = null;
  }

  window.KVCart = {
    __kv: true,
    add: add,
    change: change,
    refresh: refresh,
    open: function () { open(); },
    close: close,
    toggle: function () { isOpen() ? close() : open(); },
    isOpen: isOpen,
    getCart: function () { return state.cart; },
    formatMoney: function (c) { return formatMoney(c); },
  };

  bind();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
